import React, { useEffect, useState, useRef } from "react";
import { Box, IconButton, Tooltip, Typography, Stack } from "@mui/material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

interface EditableRichTextProps {
  value: string;
 
  onSave: (newValue: string) => void;
  onDelete?: () => void;
  isLoading: boolean;
  onCopy?: () => void;
  enableEditing?: boolean; // האם להראות אייקונים
}

const EditableRichText: React.FC<EditableRichTextProps> = ({
  value,

  onSave,
  onDelete,
  isLoading,
  onCopy,
  enableEditing = false,
}) => {
  const [internalValue, setInternalValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const editorRef = useRef<ReactQuill | null>(null);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing) {
      setTimeout(() => {
        editorRef.current?.focus();
      }, 100);
    }
  }, [isEditing]);

  const handleSave = () => {
    if (internalValue !== value) {
      onSave(internalValue); // ⬅️ שולח את הערך החדש
    }
    setIsEditing(false);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%", direction: "rtl" }}>
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        {isEditing ? (
          <Tooltip title="שמור">
            <IconButton size="small" onClick={handleSave} disabled={isLoading}>
              <CheckIcon />
            </IconButton>
          </Tooltip>
        ) : (
          enableEditing && (
            <Tooltip title="ערוך">
              <IconButton size="small" onClick={() => setIsEditing(true)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          )
        )}

        {enableEditing && onDelete && !isEditing && (
          <Tooltip title="מחק">
            <IconButton size="small" onClick={onDelete}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}

        {enableEditing && onCopy && !isEditing && (
          <Tooltip title="העתק">
            <IconButton size="small" onClick={onCopy}>
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {isEditing ? (
        <ReactQuill
          ref={(el) => (editorRef.current = el)}
          value={internalValue}
          onChange={setInternalValue}
          theme="snow"
          placeholder= 'Compose an epic...'
          style={{ minHeight: "120px",width: "100%", textAlign: "right" }}
          modules={{
            toolbar: [
              [{ size: ["small", false, "large", "huge"] }], // כפתור גודל טקסט
              ["bold", "italic", "underline"],
              [{ list: "bullet" }],
              [{ align: [] }], // כפתור יישור
              ["clean"], // איפוס עיצוב
            ],
          }}
          formats={[
            "size", "bold", "italic", "underline",
            "list", "bullet", "align", "clean"
          ]}
        />
      ) : (
        <Typography
          variant="body1"
          component="div"
          sx={{ whiteSpace: "normal", textAlign: "right" }}
          dangerouslySetInnerHTML={{ __html: value }}
        />
      )}
    </Box>
  );
};

export default EditableRichText;
